<template>
  <div ref="navigationDrawer">
    <v-navigation-drawer
      id="app-drawer"
      label="hamburger-menu"
      v-model="isBurgerMenuOpen"
      class="burger-navigation"
      floating
      temporary
      title="Burger menu"
      :aria-hidden="!isBurgerMenuOpen"
      :tabindex="isBurgerMenuOpen ? 0 : -1"
      :inert="!isBurgerMenuOpen"
      @update:modelValue="toggleBurgerMenu"
      @keydown.esc="closeBurgerMenu"
    >
      <v-list class="burger-menu--list" tag="ul" role="list">
        <li>
          <v-list-item :title="t(l.menu.my_page)" :to="{ name: 'myPage' }" ripple class="burger-menu--item gc-4">
            <template #prepend>
              <FontAwesomeIcon icon="fa-thin fa-circle-user" size="lg" />
            </template>
          </v-list-item>
        </li>
        <li>
          <v-list-item class="burger-menu--item gc-4" :href="homeUrl" target="_blank" ripple>
            <template #prepend>
              <FontAwesomeIcon width="20" icon="fa-thin fa-grid" size="lg" />
            </template>
            <v-list-item-title class="list-item-title-left-aligned">
              {{ t(l.menu.dashboard) }}
            </v-list-item-title>
          </v-list-item>
        </li>
        <li>
          <v-list-item class="gc-4 burger-menu--item" :title="t(l.menu.settings)" :href="settingsUrl" target="_blank">
            <template #prepend>
              <FontAwesomeIcon icon="fa-thin fa-gear" size="lg" />
            </template>
          </v-list-item>
        </li>
        <li>
          <v-list-item
            v-if="teacherGuideUrl && showTeacherGuide && userIsNotStudent"
            class="burger-menu--item gc-4"
            :title="t(l.menu.teacher_guide)"
            :href="teacherGuideUrl"
            target="_blank"
            ripple
          >
            <template #prepend>
              <FontAwesomeIcon icon="fa-thin fa-book-open-reader" size="lg" />
            </template>
          </v-list-item>
        </li>
        <li>
          <v-list-item v-if="supportUrl" class="burger-menu--item gc-4" :href="supportUrl" target="_blank" ripple>
            <template #prepend>
              <FontAwesomeIcon icon="fa-thin fa-circle-question" size="lg" />
            </template>
            <v-list-item-title class="list-item-title-left-aligned">
              {{ t(l.menu.support) }}
            </v-list-item-title>
          </v-list-item>
        </li>
        <li>
          <v-list-item
            v-if="feedbackFormUrl && userIsNotStudent"
            class="burger-menu--item gc-4"
            :href="feedbackFormUrl"
            target="_blank"
            ripple
          >
            <template #prepend>
              <FontAwesomeIcon icon="fa-thin fa-message-exclamation" size="lg" />
            </template>
            <v-list-item-title class="list-item-title-left-aligned">
              {{ t(l.menu.feedback_form) }}
            </v-list-item-title>
          </v-list-item>
        </li>
        <li>
          <v-list-item v-if="showDarkModeToggle">
            <template #prepend>
              <FontAwesomeIcon class="me-5" icon="fa-thin fa-moon" size="lg" />
            </template>
            <v-list-item-title> Dark mode </v-list-item-title>
            <template #append>
              <v-switch
                v-model="darkMode"
                hide-details
                inset
                @update:modelValue="toggleDarkMode"
                aria-label="Dark mode toggle"
              />
            </template>
          </v-list-item>
        </li>
      </v-list>

      <template #append>
        <div class="pa-4">
          <v-btn class="text-none button-log-out-user" block color="secondary" @click="onLogout">
            {{ t(l.button_log_out_user) }}
          </v-btn>
          <div class="version-number-text">
            {{ t(l.label_version_number) + ': ' + appVersion }}
          </div>
        </div>
      </template>
    </v-navigation-drawer>
  </div>
</template>

<script setup lang="ts">
import { useAuthentication, useUserContext } from '@ilteducation/ilt-auth';
import { computed, nextTick, ref, watch } from 'vue';
import { useLocale } from '@/plugins/translation';
import useGuiStore from '@/store/gui';
import { storeToRefs } from 'pinia';
import useLogout from '@/foundations/helpers/use-logout';
import { useTheme } from 'vuetify';
import config from '../../config/ConfigProvider';
// @ts-ignore - this is the recommended way to import it - https://www.npmjs.com/package/@vueuse/integrations
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import useAppVersion from '@/composables/use-app-version';

const { appVersion } = useAppVersion();
const userContext = useUserContext();
const showDarkModeToggle = computed(() => userContext.hasScope('ilt.content.write'));
const theme = useTheme();
const darkMode = ref(theme.global.current.value.dark);

const toggleDarkMode = () => {
  theme.global.name.value = darkMode.value ? 'baseDark' : 'light';
};

const homeUrl = config.value('homeUrl');
const settingsUrl = config.value('settingsUrl');
const supportUrl = config.value('supportUrl');
const feedbackFormUrl = config.value('feedbackFormUrl');
const showTeacherGuide = config.value('showTeacherGuide');
const teacherGuideUrl = config.value('teacherGuideUrl');

const guiStore = useGuiStore();
const { isBurgerMenuOpen } = storeToRefs(guiStore);
const { toggleBurgerMenu, closeBurgerMenu } = guiStore;
const logout = useLogout();

const userIsNotStudent = computed(() => !userContext.hasRole('student'));
const { t, l } = useLocale();

const { createLogoutLink } = useAuthentication();

const onLogout = async () => {
  await logout();
  window.location.href = createLogoutLink(window.location.href);
};

const navigationDrawer = ref(null);
const { activate: activateDrawerFocus, deactivate: deactivateDrawerFocus } = useFocusTrap(navigationDrawer, {
  allowOutsideClick: true,
});
const moveFocusToDrawer = () => {
  if (isBurgerMenuOpen.value) {
    nextTick(() => {
      if (navigationDrawer.value) {
        activateDrawerFocus();
      } else {
        // Try again until the element is available
        moveFocusToDrawer();
      }
    });
  }
};
watch(isBurgerMenuOpen, () => {
  if (isBurgerMenuOpen.value) {
    moveFocusToDrawer();
  } else {
    nextTick(() => {
      deactivateDrawerFocus();
    });
  }
});
</script>

<style scoped lang="scss">
.burger-menu--list > .burger-menu--item {
  grid-template-columns: 24px 1fr auto;
}
.burger-navigation {
  display: flex;
  flex-direction: column;
  z-index: 20;
}

.v-list-item {
  transition: all 0.3s ease;

  .v-list-item__title,
  .v-icon {
    color: #294053 !important;
  }

  svg {
    path {
      transition: all 0.3s ease;
    }
  }

  &:before,
  &:after {
    display: none;
  }

  &:hover {
    .v-list-item__title,
    .v-icon {
      color: rgba(var(--v-theme-brand));
    }

    svg {
      path {
        fill: rgb(var(--v-theme-brand)) !important;
      }
    }
  }

  &__icon {
    margin-right: 16px !important;
  }
}

.list-item-title-left-aligned {
  text-align: start;
}

.v-navigation-drawer__content > .v-list {
  padding-top: 0;
}

.v-toolbar__content button:focus {
  outline: 1px solid #5f30c5;
}

.button-log-out-user {
  letter-spacing: normal;
  font-size: 0.875rem;
  border-radius: 4px;
}

.version-number-text {
  font-size: 0.75rem;
  margin-top: 12px;
  margin-bottom: 8px;
  text-align: center;
  color: rgb(var(--v-theme-on-surface));
  opacity: 0.7;
}

.inner-list {
  .v-list-group__items {
    .v-list-item {
      padding-left: 42px !important;
      cursor: pointer;
    }
  }
}
</style>
